import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Waves, Mail } from "lucide-react";
import { OceanBackground } from "@/components/AppShell";
import { LanguageToggle } from "@/components/LanguageToggle";
import { useLanguage } from "@/i18n/LanguageContext";
import { legalUi, LEGAL_META } from "@/legal/legalContent";

const SectionBlock = ({ section }) => (
  <section className="mt-7">
    <h2 className="text-base font-semibold tracking-tight text-white/95">{section.h}</h2>
    {(section.p || []).map((para) => (
      <p
        key={para.slice(0, 40)}
        className="mt-2.5 text-sm leading-relaxed text-[hsl(var(--muted-foreground))]"
      >
        {para}
      </p>
    ))}
    {section.list && (
      <ul className="mt-2.5 space-y-1.5 pl-1">
        {section.list.map((item) => (
          <li key={item.slice(0, 40)} className="flex gap-2 text-sm leading-relaxed text-[hsl(var(--muted-foreground))]">
            <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-300/70" aria-hidden="true" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    )}
    {(section.after || []).map((para) => (
      <p
        key={para.slice(0, 40)}
        className="mt-2.5 text-sm leading-relaxed text-[hsl(var(--muted-foreground))]"
      >
        {para}
      </p>
    ))}
  </section>
);

/**
 * Phase 9: shared public legal page layout.
 * Fully public — requires NO local profile and never redirects to the app.
 */
export const LegalLayout = ({ content, testId }) => {
  const { lang } = useLanguage();
  const ui = legalUi[lang] || legalUi.en;
  const doc = content[lang] || content.en;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="relative min-h-screen" data-testid={testId}>
      <OceanBackground />
      <div className="relative mx-auto w-full max-w-2xl px-5 py-8 pb-16">
        {/* Brand header — never translated */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-cyan-300/15 border border-cyan-300/30 flex items-center justify-center">
              <Waves className="text-cyan-300" size={17} />
            </div>
            <span className="text-[11px] uppercase tracking-[0.28em] text-cyan-300/90 font-semibold">
              Blue Ocean Motion
            </span>
          </div>
          <LanguageToggle />
        </div>

        <h1 data-testid="legal-page-title" className="mt-8 text-3xl font-semibold tracking-tight">
          {doc.title}
        </h1>
        <p className="mt-2 text-xs text-[hsl(var(--muted-foreground))]">
          {ui.updatedLabel}: {LEGAL_META.updated}
        </p>

        {doc.sections.map((s) => (
          <SectionBlock key={s.h} section={s} />
        ))}

        {/* Contact block */}
        <div className="mt-10 rounded-2xl bg-white/[0.05] border border-white/10 p-4">
          <p className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
            {ui.contactLabel}
          </p>
          <a
            data-testid="legal-contact-email"
            href={`mailto:${LEGAL_META.email}`}
            className="mt-1.5 inline-flex items-center gap-2 text-sm font-medium text-cyan-300 hover:text-cyan-200 transition-colors"
          >
            <Mail size={15} /> {LEGAL_META.email}
          </a>
        </div>

        {/* Footer: cross-links + open app */}
        <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
          <Link data-testid="legal-link-privacy" to="/privacy" className="text-[hsl(var(--muted-foreground))] hover:text-white transition-colors underline underline-offset-4">
            {ui.privacy}
          </Link>
          <Link data-testid="legal-link-terms" to="/terms" className="text-[hsl(var(--muted-foreground))] hover:text-white transition-colors underline underline-offset-4">
            {ui.terms}
          </Link>
          <Link data-testid="legal-link-delete-data" to="/delete-data" className="text-[hsl(var(--muted-foreground))] hover:text-white transition-colors underline underline-offset-4">
            {ui.deleteData}
          </Link>
          <Link data-testid="legal-open-app" to="/" className="ml-auto font-semibold text-cyan-300 hover:text-cyan-200 transition-colors">
            {ui.openApp} →
          </Link>
        </div>
      </div>
    </div>
  );
};
